import {Feedback, GENERAL_TRAY_CADDY_LOCATIONS, TaskType} from 'constants/dataEnum';
import {getDeckNumber} from 'contents/ProtocolEditor/VirtualTray/labwareHelper';
import {getSelectedCard, getTasksInCard} from 'lib/cardAndTaskHelper';
import {reorderID} from 'lib/objectHelper';
import {parseMessage} from 'lib/stringHelper';
import _ from 'lodash';
import {batch} from 'react-redux';
import {showSnackbar} from 'stores/actions/appStateAction';
import {restoreCardTaskState, setIgnoredCardTask, setTaskIsSelected} from 'stores/actions/cardStateAction';
import {setCustomTipCount, setSelectedTipChannel} from 'stores/actions/protocolEditorAction';
import {resetSelectedTasks, setSelectedTask} from 'stores/actions/taskListStateAction';
import {hideAspirateDispenseVolumeModal, setFocusedDeckNumber} from 'stores/actions/virtualTrayAction';
import {checkTipVolumeAndShowAspirateDispenseModal} from 'stores/business/aspirateDispenseVolumeModalBusiness';
import {restoreTipCountForCards, restoreTipCountForSingleCard} from 'stores/business/protocolManagementBusiness';
import {
  addGeneralTask,
  checkAndSetSelectedTipChannelByCardId,
  checkAndSetSelectedTipChannelByTask,
  checkInvalidTask,
  editSimpleTransferVolumeTask,
  refreshCardIgnoreStatus,
  rearrangeTaskItemList,
  unselectAllTaskInCard,
} from 'stores/business/taskListBusinessAction';
import {globalAddUndoItem} from 'stores/business/undoRedoBusinessAction';
import {isUserOperator} from 'stores/business/userManagementBusiness';
import {getCardById} from 'stores/selectors/cardStateSelector';

const checkTaskChangesAndDoEditTask = (editedTaskResult, selectedCard, task) => (dispatch) => {
  const {taskType, volume} = editedTaskResult;
  let wasChangesMade = task.volume !== volume || task.type !== taskType;

  if (task.type === TaskType.MIXING) {
    wasChangesMade = task.mixingVolume !== volume;
  }

  if (wasChangesMade) {
    dispatch(editSimpleTransferVolumeTask(selectedCard.ID, task.ID, volume, taskType));
  }

  return wasChangesMade;
};

// eslint-disable-next-line import/prefer-default-export
export const onEditSimpleTransferHandler = (task, cardId, onApplyTask) => async (dispatch, getState) => {
  const {cardState} = getState();
  const {cards} = cardState;
  const {labwareID, type} = task;

  let selectedCard = getSelectedCard(cardState.cards);
  if (selectedCard.length > 1) {
    selectedCard = selectedCard.find((card) => card.ID === cardId);
  } else {
    [selectedCard] = selectedCard;
  }

  const deckNumber = getDeckNumber(labwareID);

  const onEditTaskApplied = (result) => {
    const isShowEditSuccessSnackbar = type !== TaskType.AIR_GAP;
    const wasChangesMade = dispatch(checkTaskChangesAndDoEditTask(result, selectedCard, task));

    if (wasChangesMade && isShowEditSuccessSnackbar) {
      const snackbarMsg = parseMessage('protocolEditor.taskList.task.editSuccess');
      dispatch(showSnackbar(Feedback.SUCCESS, snackbarMsg));
    }
    dispatch(restoreTipCountForSingleCard(selectedCard.ID));

    dispatch(unselectAllTaskInCard(selectedCard));
    dispatch(setFocusedDeckNumber(null));
    dispatch(globalAddUndoItem());

    if (!_.isNil(cardId)) {
      dispatch(checkAndSetSelectedTipChannelByCardId(cardId));
    }

    if (onApplyTask) onApplyTask();

    dispatch(hideAspirateDispenseVolumeModal());
  };

  // wait for re-render to get new deckLabwareRefs
  await batch(async () => {
    if (!_.isNil(cardId)) {
      dispatch(checkAndSetSelectedTipChannelByCardId(cardId, true));
    } else {
      dispatch(checkAndSetSelectedTipChannelByTask(task));
    }

    batch(() => {
      dispatch(unselectAllTaskInCard(cards[cardId]));
      dispatch(resetSelectedTasks());

      dispatch(setTaskIsSelected(task.ID, true));
      dispatch(setSelectedTask({taskID: task.ID, taskNumber: task.number, cardID: cardId}));
    });

    dispatch(setFocusedDeckNumber(parseInt(deckNumber, 10)));
  });

  dispatch(checkTipVolumeAndShowAspirateDispenseModal(selectedCard, task, onEditTaskApplied));
};

export const onTaskIgnoreChange = (cardId, taskId, checked) => async (dispatch) => {
  batch(() => {
    dispatch(setIgnoredCardTask(taskId, !checked));
    dispatch(refreshCardIgnoreStatus(cardId));
    dispatch(checkInvalidTask(cardId));
    dispatch(restoreTipCountForCards([cardId]));
  });
};

export const onTaskItemDrop = () => async (dispatch, getState) => {
  const {cardState} = getState();
  const {tasks} = cardState;

  const newTasksState = reorderID(tasks);
  await dispatch(restoreCardTaskState(newTasksState));
};

export const swapTaskPosition = (itemID, targetID, card) => (dispatch) => {
  const {tasks} = card;

  if (Math.abs(targetID - itemID) <= 1) {
    const originalTask = tasks[itemID];
    const targetTaskSwap = tasks[targetID];

    dispatch(rearrangeTaskItemList(originalTask, targetTaskSwap, card.ID));

    return;
  }

  if (targetID > itemID) {
    for (let i = itemID; i < targetID; i += 1) {
      dispatch(rearrangeTaskItemList(tasks[i + 1], tasks[i], card.ID));

      [tasks[i + 1], tasks[i]] = [tasks[i], tasks[i + 1]];
    }
  } else {
    for (let i = itemID; i > targetID; i -= 1) {
      dispatch(rearrangeTaskItemList(tasks[i - 1], tasks[i], card.ID));

      [tasks[i - 1], tasks[i]] = [tasks[i], tasks[i - 1]];
    }
  }
};

export const disableSwapTaskPosition = (tasks) => (dispatch) => {
  const unfinishedDelayTask = tasks.filter((task) => task.type === TaskType.DELAY && task?.delayTime === 0);
  const unfinishedConfirmationTask = tasks.filter(
    (task) =>
      (task.type === TaskType.USER_CONFIRMATION || task.type === TaskType.USER_CONFIRMATION_EJECT) &&
      task?.message === '',
  );

  return unfinishedDelayTask.length || unfinishedConfirmationTask.length || dispatch(isUserOperator());
};

export const getAirGapLabwareID = (taskId, card) => (dispatch, getState) => {
  const tasksInArr = getTasksInCard(card, getState().cardState.tasks, {isReturnArray: true});
  const airgapIndex = taskId ? tasksInArr.findIndex((otherTask) => otherTask.ID === taskId) : tasksInArr.length - 1;

  const findPreviousTaskLabwareID = (numberOfTaskBefore = 1) => {
    const taskBefore = tasksInArr[airgapIndex - numberOfTaskBefore];
    const doesPreviousTaskHaveLabwareID = Boolean(taskBefore && taskBefore.labwareID);

    if (!taskBefore) {
      return GENERAL_TRAY_CADDY_LOCATIONS[0];
    }

    if (!doesPreviousTaskHaveLabwareID) {
      return findPreviousTaskLabwareID(numberOfTaskBefore + 1);
    }

    return taskBefore?.labwareID;
  };

  return findPreviousTaskLabwareID();
};

export const addAirGapTask = (selectedCard) => async (dispatch, getState) => {
  const {cardState} = getState();
  const {tasks} = cardState;

  const insertTask = Object.values(tasks).find((task) => task.type === TaskType.INSERT_TASK);
  const airgapLabwareID = dispatch(getAirGapLabwareID(insertTask?.ID, selectedCard));

  const taskData = {
    volume: 0.1,
    isIgnored: false,
    tipPosition: 3,
    speed: 3,
    retractSpeed: 20,
    labwareID: airgapLabwareID,
    type: TaskType.AIR_GAP,
  };

  await dispatch(
    addGeneralTask(selectedCard.ID, selectedCard.type, taskData, (newlyCreatedTask) => {
      dispatch(
        onEditSimpleTransferHandler(newlyCreatedTask, selectedCard.ID, () => {
          const udpatedCard = getCardById(selectedCard.ID)(getState());

          batch(() => {
            dispatch(unselectAllTaskInCard(udpatedCard));
            dispatch(resetSelectedTasks());
          });
        }),
      );
    }),
  );
};

export const exitSimpleTransferCreationMode = () => (dispatch) => {
  dispatch(setSelectedTipChannel(null));
  dispatch(setCustomTipCount(null));
};
